/**
 * This source code is quoted from rc-slider.
 * homepage: https://github.com/react-component/slider
 */
import React from 'react';
// import {findDOMNode} from 'react-dom';
import keyCode from '../../wui-core/src/utils/keyCode';
import type {GenericSliderProps} from './iSlider'

type EventType = React.TouchEvent | React.MouseEvent | React.KeyboardEvent | React.FocusEvent;

// DOM 查询缓存
const domCache = new Map<string, HTMLElement | null>();

// 清理缓存的方法（可以在组件卸载时调用）
export function clearDomCache() {
    domCache.clear();
}

export function isEventFromHandle(e: EventType, handles: {[key: string]: any}) {
    return Object.keys(handles)
        .some(key => {
            const handle = handles[key];
            // handles 可能包含 DOM 元素或 React 元素
            if (handle instanceof HTMLElement) {
                // 如果是 DOM 元素，直接比较
                return e.target === handle;
            } else if (handle?.props?.domKey) {
                // 如果是 React 元素，通过 domKey 查找
                // 使用缓存避免重复查询
                const cacheKey = handle.props.domKey;
                let handleDom = domCache.get(cacheKey);

                if (!handleDom) {
                    // 注意：React 会将 domKey 转换为小写的 domkey
                    handleDom = document.querySelector(`[domkey="${cacheKey}"]`) as HTMLElement | null;
                    if (handleDom) {
                        domCache.set(cacheKey, handleDom);
                    }
                }

                return e.target === handleDom;
            }
            return false;
        });
}

export function isValueOutOfRange(value: number, {min, max}: Pick<GenericSliderProps, 'min' | 'max'>) {
    return value < min || value > max;
}

export function isNotTouchEvent(e: React.TouchEvent) {
    // event.touches，多点触碰时的位置数组，比如缩放手势必须要用两指的触摸点，就是一个数组
    return e.touches.length > 1 ||
		(e.type.toLowerCase() === 'touchend' && e.touches.length > 0);
}

export function getClosestPoint(val: number, {marks, step, min}: Pick<GenericSliderProps, 'min' | 'marks' | 'step'>) {
    const points = Object.keys(marks || {}).map(parseFloat);
    if (step !== null) {
        const closestStep =
			Math.round((val - min) / step) * step + min;// Math.round(((val - min) / step) * step) + min;
        points.push(closestStep);
    }
    const diffs = points.map(point => Math.abs(val - point));
    return points[diffs.indexOf(Math.min(...diffs))];
}

export function getPrecision(step: number) {
    const stepString = step.toString();
    let precision = 0;
    if (stepString.indexOf('.') >= 0) {
        precision = stepString.length - stepString.indexOf('.') - 1;
    }
    return precision;
}

export function getMousePosition(vertical: boolean, e: React.MouseEvent, zoom: number) {
    // 处理设置zoom属性导致body尺寸变大,而鼠标位置是对于window的(没有改变),这里让鼠标位置除以zoom
    zoom = zoom || (document.body.style as any).zoom || 1;
    return vertical ? e.clientY / zoom : e.pageX / zoom;
}

export function getTouchPosition(vertical: boolean, e: React.TouchEvent) {
    return vertical ? e.touches[0].clientY : e.touches[0].pageX;
}

export function getHandleCenterPosition(vertical: boolean, handle: HTMLElement) {
    const coords = handle.getBoundingClientRect();
    return vertical ?
        coords.top + (coords.height * 0.5) :
        coords.left + (coords.width * 0.5);
}

export function ensureValueInRange(val: number, {max, min}: Pick<GenericSliderProps, 'min' | 'max'>) {
    if (val <= min) {
        return min;
    }
    if (val >= max) {
        return max;
    }
    return val;
}

export function ensureValuePrecision(val: number, props: GenericSliderProps) {
    const {step} = props;
    const closestPoint = getClosestPoint(val, props);
    // 添加对 undefined 的检查
    if (closestPoint === undefined) {
        return val;
    }
    return step === null ? closestPoint :
        parseFloat(closestPoint.toFixed(getPrecision(step)));
}

export function pauseEvent(e: EventType) {
    e.stopPropagation();
    e.preventDefault();
}

export function getKeyboardValueMutator(e: React.KeyboardEvent) {
    // 优先使用 keyCode，如果没有则尝试根据 key 属性映射
    let code = e.keyCode;

    // 如果 keyCode 不存在或为 0，尝试使用 key 属性
    if (!code || code === 0) {
        const keyMap: Record<string, number> = {
            'ArrowUp': keyCode.UP,
            'ArrowRight': keyCode.RIGHT,
            'ArrowDown': keyCode.DOWN,
            'ArrowLeft': keyCode.LEFT,
            'End': keyCode.END,
            'Home': keyCode.HOME,
            'PageUp': keyCode.PAGE_UP,
            'PageDown': keyCode.PAGE_DOWN
        };
        code = keyMap[e.key] || 0;
    }

    switch (code) {
        case keyCode.UP:
        case keyCode.RIGHT:
            return (value: number, props: GenericSliderProps):number => value + props.step!;

        case keyCode.DOWN:
        case keyCode.LEFT:
            return (value: number, props: GenericSliderProps):number => value - props.step!;

        case keyCode.END:
            return (_value: number, props: GenericSliderProps):number => props.max;
        case keyCode.HOME:
            return (_value: number, props: GenericSliderProps):number => props.min;
        case keyCode.PAGE_UP:
            return (value: number, props: GenericSliderProps):number => value + props.step! * 2;
        case keyCode.PAGE_DOWN:
            return (value: number, props: GenericSliderProps):number => value - props.step! * 2;

        default:
            return undefined;
    }
}
